import { SearchableSelectOption } from './searchable-select.component';

function normalizeSearchText(text: string): string {
  return text.trim().toUpperCase();
}

/**
 * Resolves a single option from typed text (exact label, unique partial, or unique prefix).
 */
export function resolveOptionFromSearchQuery(
  query: string,
  options: SearchableSelectOption[],
  getLabel: (option: SearchableSelectOption) => string
): SearchableSelectOption | undefined {
  const q = normalizeSearchText(query);
  if (!q) {
    return undefined;
  }

  const enabled = options.filter(option => !option.disabled);

  const exact = enabled.find(
    option => normalizeSearchText(getLabel(option)) === q
  );
  if (exact) {
    return exact;
  }

  const partial = enabled.filter(option => {
    const label = normalizeSearchText(getLabel(option));
    const value = normalizeSearchText(String(option.value ?? ''));
    return label.includes(q) || value.includes(q);
  });
  if (partial.length === 1) {
    return partial[0];
  }

  const prefix = enabled.filter(option =>
    normalizeSearchText(getLabel(option)).startsWith(q)
  );
  if (prefix.length === 1) {
    return prefix[0];
  }

  return undefined;
}

export function optionsFromValues(
  values: Array<string | number>,
  config?: { translateLabels?: boolean }
): SearchableSelectOption[] {
  return values.map(value => ({
    value,
    label: String(value),
    ...(config?.translateLabels === false ? {} : {})
  }));
}

export function optionsFromTranslatedKeys(
  keys: string[]
): SearchableSelectOption[] {
  return keys.map(key => ({ value: key, label: key }));
}

export function optionsFromList<T>(
  items: T[],
  valueFn: (item: T) => string | number | null,
  labelFn: (item: T) => string,
  disabledFn?: (item: T) => boolean
): SearchableSelectOption[] {
  return items.map(item => ({
    value: valueFn(item),
    label: labelFn(item),
    disabled: disabledFn ? disabledFn(item) : false
  }));
}
